import { useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const typeColors = {
  Broad: 'bg-blue-50 text-blue-700 border-blue-200',
  Interest: 'bg-purple-50 text-purple-700 border-purple-200',
  Lookalike: 'bg-teal-50 text-teal-700 border-teal-200',
  Retargeting: 'bg-orange-50 text-orange-700 border-orange-200',
};

function ScoreBadge({ score }) {
  if (!score) return <span className="text-xs text-zinc-400">--</span>;
  const color = score >= 7 ? 'bg-emerald-100 text-emerald-700' : score >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${color}`}>{score}</span>;
}

const emptyForm = { name: '', type: 'Broad', geography: '', description: '', satisfaction_score: null, notes: '' };

export default function AdsAudiences({ items, config, reload, hasFullAccess }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('all');

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ ...emptyForm, ...item }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.patch(`/ads/audiences/${editItem.id}`, form);
        toast.success('Audience updated');
      } else {
        await api.post('/ads/audiences', form);
        toast.success('Audience created');
      }
      setShowDialog(false);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this audience?')) return;
    try { await api.delete(`/ads/audiences/${id}`); toast.success('Deleted'); reload(); }
    catch { toast.error('Failed to delete'); }
  };

  const filtered = view === 'type'
    ? [...items].sort((a, b) => (a.type || '').localeCompare(b.type || ''))
    : view === 'top'
    ? [...items].filter(a => (a.satisfaction_score || 0) >= 7)
    : items;

  return (
    <div data-testid="ads-audiences-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {['all', 'type', 'top'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${view === v ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
              data-testid={`audiences-view-${v}`}>
              {v === 'all' ? 'All' : v === 'type' ? 'By Type' : 'Top Performers'}
            </button>
          ))}
        </div>
        {hasFullAccess && (
          <Button onClick={openCreate} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="create-audience-btn">
            <Plus size={14} className="mr-1" /> New Audience
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <p className="text-lg font-medium">No audiences yet</p>
          <p className="text-sm mt-1">Define your first targeting audience</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map(aud => (
            <div key={aud.id} className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-zinc-300 transition-all group" data-testid={`audience-card-${aud.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-zinc-900">{aud.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={typeColors[aud.type] || 'bg-zinc-100 text-zinc-600'}>{aud.type}</Badge>
                    {aud.geography && <span className="flex items-center gap-1 text-xs text-zinc-500"><MapPin size={11} />{aud.geography}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ScoreBadge score={aud.satisfaction_score} />
                  {hasFullAccess && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(aud)} className="p-1.5 rounded-lg hover:bg-zinc-100"><Pencil size={14} className="text-zinc-400" /></button>
                      <button onClick={() => handleDelete(aud.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 size={14} className="text-red-400" /></button>
                    </div>
                  )}
                </div>
              </div>
              {aud.description && <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{aud.description}</p>}
              {aud.notes && <p className="text-xs text-zinc-400 mt-1 italic">{aud.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-zinc-900">{editItem ? 'Edit Audience' : 'New Audience'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-zinc-700">Name</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Audience name" className="bg-white text-zinc-900 border-zinc-300" data-testid="audience-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-700">Type</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({...f, type: v}))}>
                  <SelectTrigger className="bg-white text-zinc-900 border-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{config?.audience_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-700">Geography</Label><Input value={form.geography} onChange={e => setForm(f => ({...f, geography: e.target.value}))} placeholder="e.g. India, USA" className="bg-white text-zinc-900 border-zinc-300" /></div>
            </div>
            <div><Label className="text-zinc-700">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Age, interests, exclusions..." rows={3} className="bg-white text-zinc-900 border-zinc-300" /></div>
            <div>
              <Label className="text-zinc-700">Satisfaction Score (1-10)</Label>
              <div className="flex gap-1 mt-1">
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} onClick={() => setForm(f => ({...f, satisfaction_score: f.satisfaction_score === n ? null : n}))}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${form.satisfaction_score === n ? (n >= 7 ? 'bg-emerald-500 text-white' : n >= 4 ? 'bg-amber-500 text-white' : 'bg-red-500 text-white') : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div><Label className="text-zinc-700">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Any notes..." rows={2} className="bg-white text-zinc-900 border-zinc-300" /></div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-audience-btn">
              {saving ? 'Saving...' : editItem ? 'Update Audience' : 'Create Audience'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
